import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  CreditCard as CreditCardIcon,
  Logout as LogoutIcon,
  OpenInNew as OpenInNewIcon,
  Settings as SettingsIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  OrganizationSubscriptionStatus,
  SubscriptionStatus,
} from '../../models/Subscription';

const PAYMENT_ISSUE_STATUSES = new Set<SubscriptionStatus>([
  SubscriptionStatus.PAST_DUE,
  SubscriptionStatus.UNPAID,
]);

export interface SubscriptionManagePanelProps {
  subscription: OrganizationSubscriptionStatus;
  onOpenPortal: () => void;
  onSwitchPlan?: () => void;
  portalLoading?: boolean;
}

/**
 * Billing actions for the current plan — same strip language as the payment-method panel.
 */
const SubscriptionManagePanel: React.FC<SubscriptionManagePanelProps> = ({
  subscription,
  onOpenPortal,
  onSwitchPlan,
  portalLoading = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const status = subscription.status;
  const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd === true;
  const needsPaymentUpdate = status != null && PAYMENT_ISSUE_STATUSES.has(status);
  const canCancel =
    (status === SubscriptionStatus.ACTIVE || status === SubscriptionStatus.TRIALING) &&
    !cancelAtPeriodEnd;

  const portalLabel = cancelAtPeriodEnd
    ? t('subscription.keepSubscription', 'Keep subscription')
    : needsPaymentUpdate
      ? t('subscription.updatePayment', 'Update payment method')
      : t('subscription.openPortal', 'Manage in Stripe');

  const portalIcon =
    needsPaymentUpdate && !cancelAtPeriodEnd ? <CreditCardIcon /> : <OpenInNewIcon />;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }}
        >
          <SettingsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            component="h2"
            fontWeight={800}
            sx={{ letterSpacing: '-0.01em' }}
          >
            {t('subscription.managePanel.title', 'Manage subscription')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'subscription.managePanel.subtitle',
              'Switch plans or open Stripe to update billing and cancellation.',
            )}
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={1.25} sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25 }}>
        {onSwitchPlan && (
          <Button
            variant="contained"
            fullWidth
            startIcon={<SwapHorizIcon />}
            onClick={onSwitchPlan}
            disabled={portalLoading}
            sx={{ fontWeight: 700 }}
          >
            {t('subscription.switchPlanCta', 'Switch plan')}
          </Button>
        )}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          {canCancel ? (
            <Button
              variant="text"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              disabled={portalLoading}
              onClick={onOpenPortal}
              sx={{
                flexShrink: 0,
                px: 1.5,
                fontWeight: 700,
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                  bgcolor: alpha(theme.palette.error.main, 0.06),
                },
              }}
            >
              {t('subscription.cancelShort', 'Cancel')}
            </Button>
          ) : (
            <Box />
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={
              portalLoading ? <CircularProgress size={14} color="inherit" /> : portalIcon
            }
            disabled={portalLoading}
            onClick={onOpenPortal}
            sx={{ fontWeight: 700, flexShrink: 0 }}
          >
            {portalLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SubscriptionManagePanel;
